import { useCallback } from 'react';

import {
	ContentAndHeaderQueryArtifact,
	type ContentAndHeaderQuery$data,
	type ContentAndHeaderQuery$variables,
} from '@confluence/content-unified-query';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';
import { createEditorContentUnifiedQueryVariables } from '@confluence/editor-content-unified-query/entry-points/createEditorContentUnifiedQueryVariables.ts';
import { useAccessLinkEditMode } from '@confluence/external-share-context/entry-points/AccessLinkEditMode.ts';
import { isSpaceDenialError } from '@confluence/external-share-context/entry-points/isSpaceDenialError.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { useSpaceHomepageId } from '@confluence/space-utils/entry-points/useSpaceHomepageId.ts';
import { UseEditorContentModeValueQuery } from '@confluence/editor-content-mode-button/entry-points/UseEditorContentModeValueQuery.graphql.ts';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils.ts';
import {
	SpaceOverviewQuery,
	type SpaceOverviewQuery$data,
	type SpaceOverviewQuery$variables,
} from '@confluence/space-overview-query/entry-points/SpaceOverviewQuery.ts';

export const useForceRefetchContent = (
	contentId: string,
	spaceKey: string,
): (() => Promise<{
	spaceOverviewQueryResponse: any;
	contentAndHeaderQueryResponse: any;
	contentModeQueryResponse: any;
}>) => {
	const homepageId = useSpaceHomepageId(spaceKey);
	// True only while the Access Link route is mounted, so a constant `false` everywhere else.
	const { isAvailable: isAccessLinkRoute } = useAccessLinkEditMode();

	return useCallback(async () => {
		let spaceOverviewQueryResponse;
		let contentAndHeaderQueryResponse;
		let contentModeQueryResponse;
		if (contentId === homepageId) {
			try {
				spaceOverviewQueryResponse = await queryInterop<
					SpaceOverviewQuery$data,
					SpaceOverviewQuery$variables
				>({
					query: SpaceOverviewQuery,
					context: { single: true },
					fetchPolicy: 'network-only',
					variables: {
						spaceKey,
						excludeOperations: Boolean(!isCompanyHubSpaceKey(spaceKey)),
					},
				});
			} catch {
				// NOTE: We intentionally swallow any possible error here as this query is only aimed
				// populate the cache with just published content. Should any error occur here after
				// successful publishing the page, the Apollo cache for the query will be invalidated
				// anyway and View Page will fetch the page and handle possible errors.
			}
		}
		if (contentId) {
			try {
				const response = await queryInterop<
					ContentAndHeaderQuery$data,
					ContentAndHeaderQuery$variables
				>({
					query: ContentAndHeaderQueryArtifact,
					context: { single: true },
					fetchPolicy: 'network-only',
					variables: {
						contentId,
						spaceKey,
						useNewContentTopper: true,
					},
					// An Access Link viewer has no space membership, so the space half of this query is
					// always denied. Keep the content that came back with it.
					errorPolicy: isAccessLinkRoute ? 'all' : undefined,
				});

				// Only reachable on that route: everywhere else the default policy rejects, so a failure
				// is already in the catch. `isSpaceDenialError` requires *every* error to be a space
				// denial, so a denial arriving together with a real failure is rethrown and discarded.
				if (isAccessLinkRoute && response.error && !isSpaceDenialError(response.error)) {
					throw response.error;
				}

				contentAndHeaderQueryResponse = response;
			} catch {
				// NOTE: We intentionally swallow any possible error here as this query is only aimed
				// populate the cache with just published content. Should any error occur here after
				// successful publishing the page, the Apollo cache for the query will be invalidated
				// anyway and View Page will fetch the page and handle possible errors.
			}

			try {
				contentModeQueryResponse = await queryInterop({
					query: UseEditorContentModeValueQuery,
					context: { single: true },
					fetchPolicy: 'network-only',
					variables: expValEquals('confluence_editor_content_unified_query', 'isEnabled', true)
						? { ...createEditorContentUnifiedQueryVariables({ contentId }), isDraft: false }
						: { contentId, isDraft: false, status: ['current'] },
				});
			} catch {
				// NOTE: Content mode query is best-effort; swallow errors to keep refetch resilient.
			}
		}
		return { spaceOverviewQueryResponse, contentAndHeaderQueryResponse, contentModeQueryResponse };
	}, [contentId, spaceKey, homepageId, isAccessLinkRoute]);
};
